/**
 * @description:
 * Debounce function that limits the rate at which a function can be called.
 * This is useful for optimizing performance in scenarios where a function is called frequently, such as during window resizing or scrolling events.
 *
 * @example
 * ```typescript
 * const debouncedFunction = debounce(() => { console.log('Debounced!'); }, 2000);
 * window.addEventListener('resize', debouncedFunction);
 * ```
 *
 * @param func - The function to debounce.
 * @param wait - The delay in milliseconds (must be a non-negative number).
 * @param immediate - Whether to trigger the function on the leading edge.
 * @returns A debounced version of the input function.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number,
  immediate = false
): ((...args: Parameters<T>) => void) => {
  if (typeof wait !== 'number' || wait < 0) {
    throw new Error('The "wait" parameter must be a non-negative number.');
  }

  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>): void => {
    if (timeout) {
      clearTimeout(timeout);
    }

    const callNow = immediate && !timeout;

    timeout = setTimeout(() => {
      timeout = null;
      if (!immediate) {
        func(...args);
      }
    }, wait);

    if (callNow) {
      func(...args);
    }
  };
};
